import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { OperatingArea } from "@optimusride/client-library/@types/grpc/site";
import { Station } from "@optimusride/client-library/@types/grpc/station";
import { Station as GrpcStation } from "@optimusride/client-library/grpc/station_pb";
import { Coordinates } from "@optimusride/client-library/@types/grpc/common";
import { numberToLetter } from "../common/utils";

export type UserRoles = "admin" | "manager" | "tester";

export type User = {
    photoURL?: string;
    displayName?: string;
    commandorRole?: UserRoles;
};

export interface StationWithLetterID extends Omit<Station, "latLon"> {
    letterID: string;
    latLon: Coordinates,
}

export type Stations = {
    [keys: string]: StationWithLetterID[];
};

export type SiteDetails = {
    siteOptions: OperatingArea[];
    selectedSite?: OperatingArea;
};

interface UserState {
    user?: User;
    siteDetails: SiteDetails;
    stations: Stations;
}

const initialUserState: UserState = {
    user: undefined,
    siteDetails: {
        siteOptions: [],
    },
    stations: {},
};

export const SELECTED_SITE_LOCAL_STORAGE_KEY = "SELECTED_SITE_LOCAL_STORAGE_KEY";

export const sortAndRemapStationList = (list: (GrpcStation.AsObject | StationWithLetterID)[]) => {
    let counter = 0;
    return (
        list
            .sort((stationA, stationB) => stationA.id - stationB.id)
            .map((stationValues) => {
                const disabled = stationValues.disabledDate !== "";

                if (!disabled) {
                    counter += 1;
                }

                return ({
                    ...stationValues,
                    ...((stationValues as GrpcStation.AsObject)?.address || {}),
                    postalcode: (stationValues as GrpcStation.AsObject)?.address?.postalCode || "",
                    letterID: disabled ? "-" : numberToLetter(counter),
                    disabled,
                });
            }) as StationWithLetterID[]
    );
};

export const userSlice = createSlice({
    name: "user",
    initialState: initialUserState,
    reducers: {
        setUser: (state, payload: PayloadAction<User>) => {
            state.user = payload.payload;
        },
        setSiteDetails: (state, payload: PayloadAction<SiteDetails>) => {
            state.siteDetails = payload.payload;
        },
        setSelectedSite: (state, payload: PayloadAction<number>) => {
            window.localStorage.setItem(SELECTED_SITE_LOCAL_STORAGE_KEY, `${payload.payload}`);

            state.siteDetails = {
                ...state.siteDetails,
                selectedSite: state.siteDetails.siteOptions.find(
                    ({ id }) => id === payload.payload,
                ),
            };
        },
        setStationInfo: (state, payload: PayloadAction<Stations>) => {
            state.stations = payload.payload;
        },
        createStation: (state, {
            payload: {
                station,
                operatingArea,
            },
        }: PayloadAction<{
            station: StationWithLetterID, operatingArea: number
        }>) => {
            const previousList = state.stations[operatingArea];

            state.stations[operatingArea] = sortAndRemapStationList([...previousList, station]);

            // updates the remaining stations, with the updated station (removing or adding)
            state.stations[operatingArea].forEach((_station) => {
                if (_station.linksFromList) {
                    if (station.linksToList?.includes(_station.id)) {
                        _station.linksFromList = [...new Set<number>([
                            ..._station.linksFromList,
                            station.id,
                        ])];
                    } else {
                        _station.linksFromList = _station.linksFromList
                            .filter((_id) => _id !== station.id);
                    }
                }

                if (_station.linksToList) {
                    if (station.linksFromList?.includes(_station.id)) {
                        _station.linksToList = [...new Set<number>([
                            ..._station.linksToList,
                            station.id,
                        ])];
                    } else {
                        _station.linksToList = _station.linksToList
                            .filter((_id) => _id !== station.id);
                    }
                }
            });
        },
        updateStation: (state, {
            payload: {
                station,
                oldStationIndex,
                operatingArea,
            },
        }: PayloadAction<{
            station: StationWithLetterID,
            oldStationIndex?: number,
            operatingArea: number,
        }>) => {
            if (oldStationIndex !== undefined && oldStationIndex >= 0) {
                const newStationList = state.stations[operatingArea];

                newStationList[oldStationIndex] = station;

                state.stations[operatingArea] = sortAndRemapStationList(newStationList);

                // updates the remaining stations, with the updated station (removing or adding)
                state.stations[operatingArea].forEach((_station) => {
                    if (_station.linksFromList) {
                        if (station.linksToList?.includes(_station.id)) {
                            _station.linksFromList = [...new Set<number>([
                                ..._station.linksFromList,
                                station.id,
                            ])];
                        } else {
                            _station.linksFromList = _station.linksFromList
                                .filter((_id) => _id !== station.id);
                        }
                    }

                    if (_station.linksToList) {
                        if (station.linksFromList?.includes(_station.id)) {
                            _station.linksToList = [...new Set<number>([
                                ..._station.linksToList,
                                station.id,
                            ])];
                        } else {
                            _station.linksToList = _station.linksToList
                                .filter((_id) => _id !== station.id);
                        }
                    }
                });
            }
        },
    },
});

export const {
    setUser, setSiteDetails, setSelectedSite, setStationInfo,
    createStation, updateStation,
} = userSlice.actions;

export default userSlice.reducer;
